import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { CostBasisMethods } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { CostBasisService } from '@shared/services/cost-basis/cost-basis.service';
import { selectMultiITFromSelectedAccount } from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import { selectNewAccountRecipients } from '@store/internal-transfers/selectors/open-account/open-account.selectors';
import { selectItMultiReceivingToggle } from '@store/selectors/feature/feature.selectors';
import {
  selectInternalTransferConvertAvgCostHoldings,
  selectInternalTransferConvertSpecIDHoldings,
  selectInternalTransferFrom,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { catchError, switchMap, tap, throwError, withLatestFrom } from 'rxjs';

import {
  LoadCostBasisLots,
  LoadCostBasisLotsFail,
  LoadCostBasisLotsSuccess,
  LoadCostBasisMethodFail,
  LoadCostBasisMethodSuccess,
  LoadGiftingCostBasisMethod,
  LoadInternalTransferCostBasisMethod,
  LoadMultiInternalTransferCostBasisMethod,
} from '../../actions/cost-basis/cost-basis.actions';
import { closeSpinner, openSpinner } from '../../actions/spinner/spinner.actions';
import * as fromRootStore from '../../index';
import {
  Go,
  internalTransferAppianSubmit,
  internalTransferConvertFailed,
  internalTransferConvertHoldings,
  LogError,
  LogInfo,
  MXMConvertHoldings,
  MXMITSubmit,
  TrackProcessError,
} from '../../index';
import {
  LoadMultiITCostBasisMethodSuccess,
  StartNewAccountCollection,
} from '../../internal-transfers/actions/multi-internal-transfer.actions';
import { filterLotsData } from '../../reducers/cost-basis/cost-basis.reducer';
import { selectSelectedPositions } from '../../selectors/cost-basis/cost-basis.selectors';
import { selectedAccountsFrom } from '../../selectors/selected-client-accounts/selected-client-accounts.selectors';

@Injectable()
export class CostBasisEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private costBasisService: CostBasisService,
  ) {}

  loadGiftingCostBasisMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadGiftingCostBasisMethod),
      withLatestFrom(
        this.store$.pipe(select(selectedAccountsFrom)),
        this.store$.pipe(select(selectSelectedPositions)),
      ),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, selectedAccountsFrom, selectedPositions]) => {
        return this.costBasisService.costBasisMethodGet().pipe(
          switchMap((methodResponse) => {
            // Method response has ALL accounts
            const selectedAccount = methodResponse.accounts.find(
              // Finding the account that matches what is in state
              (account) =>
                account.accountId.toString() === selectedAccountsFrom[0].accountData.accountId,
            );

            const payload = selectedPositions.map((position) => {
              // Only save selected cost basis methods
              return selectedAccount?.holdings?.find(
                (holding) => holding.holdingIdentifier.toString() === position.positionId,
              );
            });

            return [LoadCostBasisMethodSuccess({ payload }), closeSpinner()];
          }),
          catchError(() => {
            return [
              LoadCostBasisMethodFail(),
              closeSpinner(),
              LogError({
                payload: {
                  eventId: 'error-page-catch-cost-basis-method',
                  message: 'Cost Basis Method Service Error',
                  code: LoggerCodes.COST_BASIS_FAILURE,
                },
              }),
            ];
          }),
        );
      }),
    ),
  );

  loadInternalTransferCostBasisMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadInternalTransferCostBasisMethod),
      withLatestFrom(this.store$.pipe(select(selectInternalTransferFrom))),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, selectedAccountsFrom]) => {
        if (!selectedAccountsFrom || !selectedAccountsFrom.selectedAccount) {
          return throwError(() => new Error('selectedAccountsFrom is null'));
        }
        return this.costBasisService.costBasisMethodGet().pipe(
          switchMap((methodResponse) => {
            const selectedAccount = methodResponse.accounts.find(
              (account) =>
                account.accountId.toString() ===
                selectedAccountsFrom.selectedAccount.accountData.accountId,
            );

            //As of 02/18/2025 Cash Plus accounts are not eligible for cost basis, so they will not be in the response
            if (selectedAccountsFrom.selectedAccount.accountData.cashManagementAccount) {
              return [
                LoadCostBasisMethodSuccess({ payload: [] }),
                closeSpinner(),
                fromRootStore.Next(),
              ];
            } else {
              return [
                LoadCostBasisMethodSuccess({ payload: selectedAccount?.holdings ?? [] }),
                closeSpinner(),
                fromRootStore.Next(),
              ];
            }
          }),
          catchError(() => {
            return [
              LoadCostBasisMethodFail(),
              closeSpinner(),
              LogError({
                payload: {
                  eventId: 'error-page-catch-cost-basis-method-it',
                  message: 'Cost Basis Method Service Error',
                  code: LoggerCodes.COST_BASIS_FAILURE,
                },
              }),
            ];
          }),
        );
      }),
    ),
  );

  loadMultiInternalTransferCostBasisMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadMultiInternalTransferCostBasisMethod),
      withLatestFrom(
        this.store$.pipe(select(selectMultiITFromSelectedAccount)),
        this.store$.pipe(select(selectNewAccountRecipients)),
        this.store$.pipe(select(selectItMultiReceivingToggle)),
      ),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, selectedAccount, newAccountRecipients, isMultiReceivingEnabled]) => {
        if (!selectedAccount) {
          return throwError(() => new Error('selectedAccount is null'));
        }
        return this.costBasisService.costBasisMethodGet().pipe(
          switchMap((methodResponse) => {
            const matchedAccount = methodResponse.accounts.find(
              (account) => account.accountId.toString() === selectedAccount.accountData.accountId,
            );

            //As of 02/18/2025 Cash Plus accounts are not eligible for cost basis, so they will not be in the response
            const payload = selectedAccount.accountData.cashManagementAccount
              ? []
              : (matchedAccount?.holdings ?? []);

            const actions: ReturnType<
              | typeof LoadMultiITCostBasisMethodSuccess
              | typeof closeSpinner
              | typeof StartNewAccountCollection
              | typeof fromRootStore.Next
            >[] = [LoadMultiITCostBasisMethodSuccess({ payload }), closeSpinner()];

            if (isMultiReceivingEnabled && newAccountRecipients.length > 0) {
              actions.push(
                StartNewAccountCollection({ transactionId: newAccountRecipients[0].transactionId }),
              );
            }
            actions.push(fromRootStore.Next());

            return actions;
          }),
          catchError(() => {
            return [
              LoadCostBasisMethodFail(),
              closeSpinner(),
              LogError({
                payload: {
                  eventId: 'error-page-catch-cost-basis-method-multi-it',
                  message: 'Cost Basis Method Service Error',
                  code: LoggerCodes.COST_BASIS_FAILURE,
                },
              }),
            ];
          }),
        );
      }),
    ),
  );

  loadCostBasisLots$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCostBasisLots),
      withLatestFrom(
        this.store$.pipe(select(selectedAccountsFrom)),
        this.store$.pipe(select(selectSelectedPositions)),
      ),
      switchMap(([action, selectedAccountsFrom, selectedPositions]) => {
        const { payload: positionIndex } = action;
        const accountId = selectedAccountsFrom[0].accountData.accountId;
        const positionId = selectedPositions[positionIndex].positionId;
        return this.costBasisService.costBasisLots(accountId, positionId).pipe(
          switchMap((lotsResponse) => {
            return [
              LoadCostBasisLotsSuccess({ payload: filterLotsData(lotsResponse.coveredLots) }),
              closeSpinner(),
            ];
          }),
          catchError(() => {
            return [
              LoadCostBasisLotsFail(),
              closeSpinner(),
              LogError({
                payload: {
                  eventId: 'cost-basis-lots-fail-costBasisEffect',
                  message: 'Failed to retrieve cost basis lots',
                  code: LoggerCodes.COST_BASIS_FAILURE,
                },
              }),
            ];
          }),
        );
      }),
    ),
  );

  costBasisMethodFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCostBasisMethodFail),
      switchMap(() => {
        return [
          Go({ path: ['error'] }),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-route-costBasisEffect',
              message: 'Failed to retrieve SMN cost basis data',
              code: LoggerCodes.COST_BASIS_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  mxmTransferConvertHoldings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MXMConvertHoldings),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedPositions)),
        this.store$.pipe(select(selectInternalTransferFrom)),
        this.store$.pipe(select(selectInternalTransferConvertSpecIDHoldings)),
        this.store$.pipe(select(selectInternalTransferConvertAvgCostHoldings)),
      ),
      switchMap(([, selectedCostBasisHoldingsMethod, accountFrom, specID, avgCost]) => {
        const newCostBasis = CostBasisMethods.FIFO;
        const convertSpecID = specID;
        const convertAvgCost = avgCost;
        const accountId = accountFrom.selectedAccount.accountData.accountId;
        const filteredHoldings = selectedCostBasisHoldingsMethod.filter((holding) => {
          if (convertAvgCost && holding.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST) {
            return holding;
          } else if (convertSpecID && holding.selectedCostBasisMethod === CostBasisMethods.SPECID) {
            return holding;
          }
        });

        return this.costBasisService
          .costBasisMethodPost(accountId, filteredHoldings, newCostBasis)
          .pipe(
            switchMap((res) => {
              return res.success
                ? [
                    MXMITSubmit(),
                    LogInfo({
                      payload: {
                        eventId: 'mxm-convert-cost-basis',
                        message:
                          'Cost basis holdings conversion successful, kick off MXM IT Submit',
                      },
                    }),
                  ]
                : [
                    internalTransferConvertFailed(),
                    LogError({
                      payload: {
                        eventId: 'mxm-convert-cost-basis-error',
                        message: 'Cost basis holdings conversion unsuccessful for Final MXM Signer',
                      },
                    }),
                  ];
            }),
          );
      }),
      catchError((e) => {
        return [
          internalTransferConvertFailed(),
          LogError({
            payload: {
              eventId: 'error-cost-basis-convert',
              message: `Cost Basis Effects Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  internalTransferConvertHoldings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(internalTransferConvertHoldings),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedPositions)),
        this.store$.pipe(select(selectInternalTransferFrom)),
        this.store$.pipe(select(selectInternalTransferConvertSpecIDHoldings)),
        this.store$.pipe(select(selectInternalTransferConvertAvgCostHoldings)),
      ),
      switchMap(
        ([, selectedCostBasisHoldingsMethod, accountFrom, convertSpecID, convertAvgCost]) => {
          const newCostBasis = CostBasisMethods.FIFO;
          const accountId = accountFrom.selectedAccount.accountData.accountId;
          const filteredHoldings = selectedCostBasisHoldingsMethod.filter((holding) => {
            if (
              convertAvgCost &&
              holding.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST
            ) {
              return holding;
            } else if (
              convertSpecID &&
              holding.selectedCostBasisMethod === CostBasisMethods.SPECID
            ) {
              return holding;
            }
          });

          return this.costBasisService
            .costBasisMethodPost(accountId, filteredHoldings, newCostBasis)
            .pipe(
              switchMap((res) => {
                return res.success
                  ? [
                      internalTransferAppianSubmit(),
                      LogInfo({
                        payload: {
                          eventId: 'convert-cost-basis',
                          message: 'Cost basis holdings conversion successful',
                        },
                      }),
                    ]
                  : [
                      internalTransferConvertFailed(),
                      LogError({
                        payload: {
                          eventId: 'convert-cost-basis-error',
                          message: 'Cost basis holdings conversion unsuccessful',
                        },
                      }),
                    ];
              }),
            );
        },
      ),
      catchError((e) => {
        return [
          internalTransferConvertFailed(),
          LogError({
            payload: {
              eventId: 'error-cost-basis-convert',
              message: `Cost Basis Effects Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  internalTransferConvertError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(internalTransferConvertFailed),
      switchMap(() => {
        return [
          Go({ path: ['error'] }),
          LogError({
            payload: {
              eventId: 'error-page-route-convert-failure',
              message: 'Internal transfer convert holdings failed',
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
          TrackProcessError({
            errorCode: 'coo-cost_basis_conv-fail',
          }),
        ];
      }),
    ),
  );
}
